import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";

const chf = (n) => `CHF ${Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Einstellungen → MWST: Lieferanten mit gelernter Bezugsteuer-Regel (Ja/Nein) prüfen, umstellen oder zurücksetzen. */
export function ReverseChargeSuppliersCard({ companyId, isViewer }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/vat/reverse-charge-suppliers`).then((r) => setData(r.data)).catch(() => setData({ suppliers: [] }));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const update = async (s, value) => {
    setBusy(s.id);
    try {
      const r = await apiClient.put(`/companies/${companyId}/vat/reverse-charge-suppliers/${s.id}`, { reverse_charge: value });
      toast.success(value === null ? `Regel für ${s.name} zurückgesetzt – der nächste Beleg fragt wieder` : `${s.name}: Bezugsteuer ${value ? "JA" : "NEIN"}` + (r.data.rechecked_inbox ? ` · ${r.data.rechecked_inbox} offene Belege neu geprüft` : ""));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte nicht speichern"); } finally { setBusy(null); }
  };
  const rows = data?.suppliers || [];
  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="settings-reverse-charge-suppliers">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2"><Globe className="h-4 w-4 text-indigo-600" />Bezugsteuer – Lieferanten-Regeln</h3>
          <p className="text-xs text-slate-500 mt-0.5">Beim ersten Beleg eines ausländischen Dienstleisters entscheidest du Ja/Nein; die Regel gilt danach automatisch für alle weiteren Belege. Hier prüfen, umstellen oder zurücksetzen.</p>
        </div>
        {data?.year && <Badge variant="outline" className="text-[11px]">Zählung {data.year}</Badge>}
      </div>
      {rows.length === 0 ? (
        <div className="mt-3 text-xs text-slate-500" data-testid="reverse-charge-suppliers-empty">Noch keine Lieferanten-Regel gelernt.</div>
      ) : (
        <div className="mt-3 rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {rows.map((s) => (
            <div key={s.id} className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 text-xs" data-testid={`rc-supplier-${s.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{s.name}{s.country ? <span className="text-slate-400 font-normal"> · {s.country}</span> : null}</div>
                <div className="text-slate-500">{s.count} Beleg{s.count === 1 ? "" : "e"} {data.year} · {chf(s.net_chf)}{s.last_date ? ` · zuletzt ${s.last_date}` : ""}{s.pending_inbox ? ` · ${s.pending_inbox} offen im Posteingang` : ""}{s.updated_at ? ` · Regel vom ${String(s.updated_at).slice(0, 10)}` : ""}</div>
              </div>
              <Badge className={s.reverse_charge ? "bg-indigo-600 hover:bg-indigo-600" : "bg-slate-500 hover:bg-slate-500"} data-testid={`rc-supplier-${s.id}-flag`}>{s.reverse_charge ? "Bezugsteuer JA" : "Bezugsteuer NEIN"}</Badge>
              {!isViewer && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === s.id} onClick={() => update(s, !s.reverse_charge)} data-testid={`rc-supplier-${s.id}-toggle`}>
                    Auf {s.reverse_charge ? "NEIN" : "JA"} stellen
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-600" disabled={busy === s.id} onClick={() => update(s, null)} title="Regel löschen – nächster Beleg fragt wieder" data-testid={`rc-supplier-${s.id}-reset`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />Zurücksetzen
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate-500 inline-flex items-center gap-1"><FileText className="h-3 w-3" />Die ESTV-Meldung (PDF) für nicht registrierte Firmen findest du im Posteingang und bei den Belegen in der Bezugsteuer-Leiste.</div>
    </div>
  );
}
